<template>
  <DialogRoot v-model:open="isOpen" modal>
    <DialogTrigger v-if="$slots.trigger" asChild>
      <slot name="trigger" />
    </DialogTrigger>

    <DialogPortal>
      <Transition name="overlay">
        <DialogOverlay class="ModalOverlay" />
      </Transition>

      <Transition name="modal">
        <DialogContent
          ref="modalContent"
          class="Modal"
          :class="{
            'Modal--persistent': persistent,
            'Modal--shake': isShaking,
            'Modal--drawer': drawer,
          }"
          v-bind="$attrs"
          @interact-outside="handleInteractOutside"
          @open-auto-focus="focusModalContainer"
        >
          <DialogTitle
            v-if="$slots.title || title"
            class="Modal-title"
            :class="{ 'has-icon': !!titleIconName }"
          >
            <slot name="title">
              <Icon
                v-if="titleIconName"
                :icon="titleIconName"
                :color="titleIconColor"
                :size="40"
              />

              {{ title }}
            </slot>
          </DialogTitle>

          <DialogClose
            v-if="closeCross"
            class="Modal-close"
            aria-label="Close modal"
          >
            <Icon icon="window-close" size="lg" />
          </DialogClose>

          <slot />

          <div v-if="$slots.actionPrimary || $slots.actionSecondary" class="Modal-actions">
            <slot name="actionPrimary" />
            <slot name="actionSecondary" />
          </div>

          <p v-if="$slots.disclaimer" class="Modal-disclaimer">
            <slot name="disclaimer" />
          </p>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import {
  DialogRoot,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogClose,
} from 'reka-ui';
import Icon from '../../atoms/Icon/Icon.vue';
import type { IconProps } from '../../atoms/Icon/Icon.model';
import type { ModalProps } from './Modal.model';

const props = withDefaults(defineProps<ModalProps>(), {
  closeCross: false,
  persistent: false,
  drawer: false,
});

defineOptions({ inheritAttrs: false });

const modalContent = useTemplateRef('modalContent');

const isOpen = defineModel<boolean>({ default: false });
const isShaking = ref(false);

const titleIconName = computed<IconProps['icon'] | null>(() => {
  if (!props.titleIcon) return null;
  return typeof props.titleIcon === 'string' ? props.titleIcon : props.titleIcon.icon;
});

const titleIconColor = computed<IconProps['color']>(() => {
  if (!props.titleIcon) return undefined;
  return typeof props.titleIcon === 'string' ? undefined : props.titleIcon.color;
});

watch(isOpen, (newVal: boolean) => {
  if (newVal) {
    // Fixes pointer-events being disabled on body which affects 3rd party modals
    // https://github.com/unovue/reka-ui/issues/1540
    setTimeout(() => {
      document.body.style.pointerEvents = '';
    });
  }
});

// Animation duration matching CSS + a small margin
const SHAKE_ANIMATION_THROTTLE = 500;

/**
 * Handles interaction outside the modal (like clicking the overlay).
 * If the modal is persistent, prevents closing and triggers a shake animation.
 * Otherwise allows the default behavior (closing the modal).
 *
 * @param event - The interaction event that occurred outside the modal
 */
function handleInteractOutside (event: Event) {
  if (props.persistent) {
    event.preventDefault();

    // Trigger shake animation to indicate the modal cannot be closed
    isShaking.value = true;
    setTimeout(() => {
      isShaking.value = false;
    }, SHAKE_ANIMATION_THROTTLE);
  }
};

/** Prevent focusing the first focusable element inside the modal */
function focusModalContainer(event: Event) {
  event.preventDefault();
  modalContent.value?.$el.focus();
}

/** Scroll modal's content to top */
function scrollToTop() {
  if (modalContent.value?.$el) {
    modalContent.value.$el.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

defineExpose({
  scrollToTop,
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/mixins';
@use '@cvo/ui/styles/utils/animations' as animations;

// ===========================================
// MODAL COMPONENT STYLES
// ===========================================

@layer cvo-ui {
  .ModalOverlay {
    @include mixins.set-overlay;
  }

  // Modal container
  .Modal {
    $modal-padding-inline: vars.$organisms-modal-lg-md-padding-left-right;
    $modal-padding-block: vars.$organisms-modal-lg-md-padding-top-bottom;
    $modal-padding-inline-mobile: vars.$organisms-modal-sm-padding-left-right;
    $modal-padding-block-mobile: vars.$organisms-modal-sm-padding-top-bottom;

    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    isolation: isolate;

    background-color: vars.$organisms-modal-bg-color;
    border-radius: vars.$organisms-modal-lg-md-border-radius;
    border: vars.$organisms-modal-border-stroke solid vars.$organisms-modal-border-color;
    box-shadow: vars.$shadow-lg;

    color: vars.$organisms-modal-text-color;

    max-height: 95vh;
    overflow-y: auto;

    width: 600px;
    padding-inline: $modal-padding-inline;
    padding-block: $modal-padding-block;

    transform-origin: 0 0;
    transition: scale vars.$transition-fast ease-in-out;

    @include responsive.mobile {
      width: 90%;
      border-radius: vars.$organisms-modal-sm-border-radius;
      padding-inline: $modal-padding-inline-mobile;
      padding-block: $modal-padding-block-mobile;
    }

    // Vue transition classes for modal enter/leave
    &.modal-enter-active {
      transition: opacity vars.$transition-base ease-out, scale vars.$transition-base ease-out;
    }

    &.modal-leave-active {
      transition: opacity vars.$transition-base ease-in, scale vars.$transition-base ease-in;
    }

    &.modal-enter-from {
      opacity: 0;
      scale: 0.9;
    }

    &.modal-leave-to {
      opacity: 0;
      scale: 0.9;
    }

    &:has(.Modal-close) {
      padding-top: vars.$dimensions-d-12; // Extra space for close button
    }

    // Shake animation for persistent modal interaction
    &--shake {
      @include responsive.tablet {
        animation: modalShake vars.$transition-base ease-in-out !important;
      }
    }

    // Drawer mode - slides from bottom on mobile
    &--drawer {
      @include responsive.mobile {
        width: calc(100vw - $modal-padding-inline-mobile * 2);
        max-width: 100vw;
        inset-inline: 0;
        top: auto;
        bottom: 0;
        margin: 0 $modal-padding-inline-mobile;
        transform: translateY(0);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;

        &.modal-enter-from {
          opacity: revert;
          scale: revert;
          transform: translateY(100%);
        }

        &.modal-leave-to {
          opacity: revert;
          scale: revert;
          transform: translateY(100%);
        }

        &.modal-enter-active,
        &.modal-leave-active {
          transition: opacity vars.$transition-base ease-out, transform vars.$transition-base ease-out;
        }
      }
    }

    &-title {
      color: vars.$organisms-modal-title-color;
      margin-bottom: vars.$dimensions-d-3;
      padding: 0 vars.$dimensions-d-6;
      text-align: center;

      @include text.heading-responsive('h5', 'h3');

      &.has-icon {
        display: grid;
        grid-template-columns: 40px auto 40px;
        align-items: start;
        gap: vars.$dimensions-d-4;

        @include responsive.mobile {
          grid-template-columns: 1fr;
          justify-items: center;
        }
      }
    }

    // Close button
    &-close {
      position: absolute;
      top: $modal-padding-block;
      inset-inline-end: $modal-padding-inline;
      padding: 0;
      @include layout.square($modal-padding-inline);
      display: flex;
      justify-content: center;
      align-items: center;
      background: transparent;
      border: none;
      border-radius: vars.$border-radius-sm;
      cursor: pointer;
      color: vars.$organisms-modal-icon-close-color;

      @include responsive.mobile {
        top: $modal-padding-block-mobile;
        inset-inline-end: $modal-padding-inline-mobile;
        @include layout.square($modal-padding-inline-mobile);
      }

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
        color: vars.$color-neutral-gray-800;
      }

      &:focus {
        outline: 2px solid vars.$color-brand-secondary-900;
        outline-offset: 2px;
      }
    }

    &-actions {
      display: flex;
      gap: vars.$dimensions-d-6;
      justify-content: center;
      flex-wrap: wrap;
      margin-top: vars.$dimensions-d-4;

      @include responsive.mobile {
        display: flex;
        flex-direction: column-reverse;
        gap: vars.$dimensions-d-6;
        align-items: stretch;
      }
    }

    &-disclaimer {
      margin-top: vars.$dimensions-d-8;
      text-align: center;
      color: vars.$organisms-modal-text-disclaimer-color;
      @include text.text('xs');

      > *:not(:last-child) {
        margin-bottom: vars.$dimensions-d-3;
      }
    }
  }

  // ===========================================
  // MODAL ANIMATIONS
  // ===========================================

  // Shake animation for persistent modal feedback
  @keyframes modalShake {
    50% {
      scale: 1.02;
    }
  }
}
</style>
